import React from "react";
import PropTypes from "prop-types";
import SequenceCard from "./SequenceCard";
import { SEQUENCE_GROUPS } from "../followUpConstants";
import styles from "./SequenceList.module.css";

/**
 * Aba "Minhas Cadências". Renderiza SEMPRE os dois grupos fixos por triggerType
 * (mesmo vazios), cada um com título, explicação contextual, botão de criar e
 * a lista de SequenceCard.
 *
 * Banner de aviso quando o grupo tem cadências ativas mas nenhuma marcada como
 * principal — o sistema não saberia qual usar automaticamente.
 */
function Group({
  group,
  sequences,
  metricsBySequence,
  onCreate,
  onEdit,
  onSetDefault,
  onDeactivate,
  onReactivate,
  onDelete,
}) {
  const hasActive = sequences.some((s) => s.isActive !== false);
  const hasDefault = sequences.some(
    (s) => s.isDefault && s.isActive !== false,
  );
  const showNoDefaultWarning = hasActive && !hasDefault;

  return (
    <section className={styles.group} aria-label={group.title}>
      <div className={styles.groupHeader}>
        <div className={styles.groupHeading}>
          <h2 className={styles.groupTitle}>
            <span aria-hidden="true">{group.icon}</span> {group.title}
          </h2>
          <p className={styles.groupDesc}>{group.description}</p>
        </div>
        <button
          type="button"
          className={styles.createBtn}
          onClick={() => onCreate(group.triggerType)}
        >
          <span aria-hidden="true">+</span> Criar cadência
        </button>
      </div>

      {showNoDefaultWarning && (
        <div className={styles.warning} role="alert">
          <span aria-hidden="true">⚠️</span>
          <span>
            Nenhuma cadência principal definida para este tipo. O sistema não
            saberá qual cadência usar.
          </span>
        </div>
      )}

      {sequences.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden="true">
            📭
          </span>
          <p className={styles.emptyTitle}>
            Nenhuma cadência criada para este tipo ainda
          </p>
          <p className={styles.emptyText}>
            Crie uma cadência para começar a reativar automaticamente quem não
            voltou.
          </p>
          <button
            type="button"
            className={styles.emptyCta}
            onClick={() => onCreate(group.triggerType)}
          >
            <span aria-hidden="true">+</span> Criar primeira cadência
          </button>
        </div>
      ) : (
        <div className={styles.cards}>
          {sequences.map((seq) => (
            <SequenceCard
              key={seq.id}
              sequence={seq}
              metrics={metricsBySequence[seq.id] ?? null}
              onEdit={() => onEdit(seq)}
              onSetDefault={() => onSetDefault(seq)}
              onDeactivate={() => onDeactivate(seq)}
              onReactivate={() => onReactivate(seq)}
              onDelete={() => onDelete(seq)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

Group.propTypes = {
  group: PropTypes.shape({
    triggerType: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  sequences: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  metricsBySequence: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onCreate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default function SequenceList({
  sequences = [],
  metricsBySequence = {},
  loading = false,
  onCreate,
  onEdit,
  onSetDefault,
  onDeactivate,
  onReactivate,
  onDelete,
}) {
  if (loading) {
    return (
      <div className={styles.skeletonWrap} aria-busy="true" aria-label="Carregando cadências">
        {SEQUENCE_GROUPS.map((g) => (
          <div key={g.triggerType} className={styles.skeletonGroup}>
            <div className={`${styles.skeletonLine} ${styles.skeletonTitle}`} />
            <div className={styles.skeletonCard} />
            <div className={styles.skeletonCard} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.list}>
      {SEQUENCE_GROUPS.map((group) => (
        <Group
          key={group.triggerType}
          group={group}
          sequences={sequences.filter(
            (s) => s.triggerType === group.triggerType,
          )}
          metricsBySequence={metricsBySequence}
          onCreate={onCreate}
          onEdit={onEdit}
          onSetDefault={onSetDefault}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

SequenceList.propTypes = {
  sequences: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  metricsBySequence: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  loading: PropTypes.bool,
  onCreate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
